import {
  TypeImplementation,
  withSystem,
  Plugins,
  EnvTypes,
} from '@packages/systems/wfdl';
import {
  functionOf,
  TText,
  TList,
  TListOfElement,
  TTypeConstructor,
} from '@packages/systems/wfdl-ast';
import {qualify, typeName, iconName} from '@packages/systems/wfdl-Qualified';
import {type DynamoEnvValueType} from '../types';
import {TOption} from '@packages/systems/plugins/Option';
import {TBasicLink, type ExternalLinkLiteral} from '../Link/index';
import {TDynamoVideoLink} from '@packages/systems/plugins-utils/DynamoGateway';
import {
  LinkPreloadValue,
  LinkTargetValue,
  TLinkPreload,
  TLinkTarget,
} from '../toBasicLinkV2Shared';

export const VideoLinkType = TypeImplementation.Custom({
  render: () => null,
  icon: TypeImplementation.IconByName(
    qualify(Plugins.name('Embed'), iconName('Video'))
  ),
});

const aspectRatio = (metadata: {
  height: string;
  html: string;
  thumbnail_url: string;
  width: string;
}) => {
  const height = metadata?.height;
  const width = metadata?.width;
  return height && width ? Number(height) / Number(width) : 0;
};

type RawVideoDataShape = {
  url: string;
  metadata: {
    html: string;
    height: string;
    width: string;
    thumbnail_url: string;
  };
};

export const dynamoVideoLinkToVideo = {
  envType: {
    dataType: functionOf(
      withSystem({isBindable: true}, TOption(TDynamoVideoLink)),
      TTypeConstructor(qualify(Plugins.name('Embed'), typeName('Video')))
    ),
    exported: 'visible',
    implicit: 'prefilledArgs',
  },
  value(video: DynamoEnvValueType<RawVideoDataShape>) {
    if (video === null || video.tag === 'None') {
      // Default empty Video object
      return {
        embed: {
          loaded: false,
          type: 'video',
          url: '',
          title: '',
        },
      };
    }
    const metadata = video.val.metadata;
    const html = metadata?.html;
    return {
      embed: {meta: {html}, type: 'video'},
      style: {'padding-top': `${aspectRatio(metadata) * 100}%`},
    };
  },
} as const;

export const dynamoVideoLinkToText = {
  envType: {
    dataType: functionOf(
      withSystem({isBindable: true}, TOption(TDynamoVideoLink)),
      TText
    ),
    exported: 'visible',
    implicit: 'prefilledArgs',
  },
  value(video: DynamoEnvValueType<RawVideoDataShape>) {
    if (video === null || video.tag === 'None') {
      return '';
    }
    return video.val.url;
  },
} as const;

export const dynamoVideoLinkToListOfElements = {
  envType: {
    dataType: functionOf(
      withSystem({isBindable: true}, TOption(TDynamoVideoLink)),
      TListOfElement
    ),
    exported: 'visible',
    implicit: 'prefilledArgs',
  },
  value(video: DynamoEnvValueType<RawVideoDataShape>) {
    if (video === null || video.tag === 'None') {
      return [];
    }
    return [
      {
        text: true,
        type: null,
        id: 'temp',
        children: [],
        data: {value: video.val.url},
      },
    ];
  },
} as const;

/**
 * @deprecated - replaced by the V2 implicit below
 *
 * Should be exported as hidden to ensure backwards compatibility before
 * we have run the site data migration to remove calls to this function
 * from element data.
 *
 * Should be deleted after the migration has been shipped.
 */
export const dynamoVideoLinkToBasicLink = {
  envType: {
    dataType: functionOf(
      withSystem({isBindable: true}, TOption(TDynamoVideoLink)),
      TBasicLink
    ),
    exported: 'hidden',
    implicit: 'prefilledArgs',
  },
  value: (
    option: DynamoEnvValueType<RawVideoDataShape>
  ): ExternalLinkLiteral => {
    if (option === null || option.tag === 'None') {
      return {
        url: '',
        mode: 'external',
      };
    }

    if (typeof option.val === 'object' && 'url' in option.val) {
      return {
        url: option.val.url,
        mode: 'external',
      };
    }

    return {
      url: '',
      mode: 'external',
    };
  },
} as const;

export const dynamoVideoLinkToBasicLinkV2: EnvTypes.TypedValue = {
  envType: {
    dataType: withSystem(
      {label: 'Link settings'},
      functionOf(
        TLinkTarget,
        functionOf(
          TLinkPreload,
          functionOf(
            withSystem({isBindable: true}, TOption(TDynamoVideoLink)),
            TBasicLink
          )
        )
      )
    ),
    exported: 'visible',
    implicit: 'prefilledArgs',
  },
  value: (target: LinkTargetValue) => {
    return (preload: LinkPreloadValue) => {
      return (
        option: DynamoEnvValueType<RawVideoDataShape>
      ): ExternalLinkLiteral => {
        const openInNewTabBool = target.tag === 'New tab';
        const preloadValue = preload.tag.toLowerCase() as
          | 'none'
          | 'prefetch'
          | 'prerender';
        if (option === null || option.tag === 'None') {
          return {
            url: '',
            mode: 'external',
          };
        }

        if (
          typeof option.val === 'object' &&
          'url' in option.val &&
          option.val.url
        ) {
          return {
            url: option.val.url,
            mode: 'external',
            ...(preloadValue !== 'none' ? {preload: preloadValue} : null),
            ...(openInNewTabBool ? {target: '_blank'} : {}),
          };
        }

        return {
          url: '',
          mode: 'external',
        };
      };
    };
  },
};

const emptyListLiteral = Object.freeze([]);

export const dynamoVideoLinkToListOfLightboxMedia = {
  envType: {
    dataType: functionOf(
      withSystem({isBindable: true}, TOption(TDynamoVideoLink)),
      TList(
        TTypeConstructor(
          qualify(Plugins.name('Lightbox'), typeName('LightboxMedia'))
        )
      )
    ),
    exported: 'visible',
    implicit: 'prefilledArgs',
  },
  value: (option: DynamoEnvValueType<RawVideoDataShape>) => {
    if (option === null || option.tag === 'None') {
      return emptyListLiteral;
    }

    if (
      typeof option.val === 'object' &&
      'url' in option.val &&
      'metadata' in option.val &&
      option.val.metadata != null
    ) {
      const {
        html,
        height,
        width,
        thumbnail_url: thumbnailUrl,
      } = option.val.metadata;
      return [
        {
          tag: 'Video',
          val: {
            embed: {
              url: option.val.url,
              meta: {
                height,
                html,
                width,
                thumb: {url: thumbnailUrl},
              },
            },
          },
        },
      ];
    }

    return emptyListLiteral;
  },
} as const;
